import { NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, Renderer2, ViewEncapsulation, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Event, NavigationEnd, Router } from '@angular/router';
import {
  BroadcastService,
  ClientAccessService,
  ClientConstants,
  CurrentDocumentService,
  ConfigService,
  DocumentSaveService,
  ModifiedFieldService,
  NotificationService
} from '@formbird/services';
import { DocumentData, SharedConstants } from '@formbird/types';

import { DeletionService } from '@services/deletion/deletion.service';
import { DocumentListService } from '@services/document/document-list.service';
import { NewDocumentService } from '@services/document/new-document.service';
import { Subscription } from 'rxjs';
import { DeleteComponent } from '../delete/delete.component';
import { PrintComponent } from '../print/print.component';
import { TemplateNameComponent } from '../template-name/template-name.component';
import { CommandBarUploaderComponent } from './command-bar-uploader.component';

@Component({
  selector: 'ft-command-bar',
  templateUrl: './command-bar.component.html',
  styleUrls: ['./command-bar.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  imports: [NgClass, MatButtonModule, MatIconModule, MatProgressSpinnerModule, TemplateNameComponent, CommandBarUploaderComponent, PrintComponent, DeleteComponent, MatTooltipModule]
})
export class CommandBarComponent implements OnInit, OnDestroy {
  private broadcastService = inject(BroadcastService);
  private clientAccessService = inject(ClientAccessService);
  private configService = inject(ConfigService);
  private deletionService = inject(DeletionService);
  private currentDocumentService = inject(CurrentDocumentService);
  private documentListService = inject(DocumentListService);
  private newDocumentService = inject(NewDocumentService);
  private modifiedFieldService = inject(ModifiedFieldService);
  private notificationService = inject(NotificationService);
  private documentSaveService = inject(DocumentSaveService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private renderer = inject(Renderer2);

  documentData: DocumentData;

  private subscriptions: Subscription = new Subscription();
  deleteOptions: any;

  isDirty = false;
  isSaving = false;
  showSpinner = false;

  displayState: any = {};

  summaryName = 'none';
  searchRoute = '/search/';

  constructor() {

    this.deleteOptions = {};

    this.modifiedFieldService.setUpdateAllowed(false);
    this.initializeDisplayState();
  }

  /**
   * Helper to get a formNavBar field with fallback:
   * 1. Check template.formNavBar[field]
   * 2. If undefined, check configService.clientConfig()?.formNavBar[field]
   * 3. If still undefined, return undefined
   */
  private getFormNavBarField(field: string): any {
    const templateNavBar = this.documentData?.template?.formNavBar;
    const configNavBar = this.configService.clientConfig()?.formNavBar;
    if (templateNavBar && Object.prototype.hasOwnProperty.call(templateNavBar, field)) {
      return templateNavBar[field];
    }
    if (configNavBar && Object.prototype.hasOwnProperty.call(configNavBar, field)) {
      return configNavBar[field];
    }
    return undefined;
  }

  ngOnInit() {
    const stateChangeStartSub = this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.modifiedFieldService.setFormAccess(null);
        this.initializeDisplayState();

        try {
          const searchInputEl = this.renderer.selectRootElement("ft-search-box input[type=text]");
          this.renderer.setAttribute(searchInputEl, 'value', '');
        } catch (e) {}

        this.cdr.markForCheck();
      }
    });

    const docDataSub: Subscription = this.currentDocumentService.documentData$.subscribe((data: DocumentData) => {
      this.applyDocumentData(data);
    });

    // documentData$ is a plain Subject — seed with current value so this component gets
    // the latest data when it is created after the Subject already emitted (e.g. after a
    // hideTopBar toggle destroys and recreates the command bar mid-document-lifecycle).
    const currentData = this.currentDocumentService.getDocumentData();
    if (currentData?.document) {
      this.applyDocumentData(currentData);
    }

    const delSub = this.deletionService.deletionService$.subscribe((deleteOptions: any) => {
      this.deleteOptions = deleteOptions;
      this.displayState.showDeleteButton = deleteOptions.isDeletable && this.getFormNavBarField('hideDeleteButton') !== true;
    });

    const docLoadErrorSub: Subscription = this.broadcastService.on(ClientConstants.DOCUMENT_LOAD_ERROR).subscribe(() => {
      this.modifiedFieldService.setFormAccess(null);
      this.initializeDisplayState();
    });

    const formDirtySub: Subscription = this.broadcastService.on(ClientConstants.FORM_DIRTY_UPDATED).subscribe((formDirty: any) => {
      if (formDirty) {
        this.isDirty = formDirty.dirty;
        this.cdr.detectChanges();
      }
    });

    const savingSub: Subscription = this.documentSaveService.onDocumentSaving().subscribe((isDocSaving: boolean) => {
      this.isSaving = isDocSaving;
      this.cdr.detectChanges();
    });

    const showSpinnerSub: Subscription = this.broadcastService.on(ClientConstants.SPINNER_UPDATED).subscribe((showSpinner: boolean) => {
      this.showSpinner = showSpinner;
    });

    this.subscriptions.add(stateChangeStartSub);
    this.subscriptions.add(docDataSub);
    this.subscriptions.add(delSub);
    this.subscriptions.add(docLoadErrorSub);
    this.subscriptions.add(formDirtySub);
    this.subscriptions.add(savingSub);
    this.subscriptions.add(showSpinnerSub);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  private applyDocumentData(data: DocumentData) {
    this.documentData = data;

    if (this.documentData?.document?.systemHeader) {
      if (this.documentData.document.systemHeader.currentVersion === false) {
        this.popupVersionMessage(this.documentData.document);
      }
    }

    const document = this.documentData.document;
    const template = this.documentData.template;

    if (document) {
      this.modifiedFieldService.setFormAccess(document);
      this.deletionService.checkingDeleteDoc(document);
    }

    const canCreate = this.clientAccessService.checkAccess(this.documentData.template, SharedConstants.OPERATION_TYPE_CREATE);

    this.summaryName = document?.systemHeader?.summaryName || template?.systemHeader?.summaryName;

    this.displayState.showCommandBar = this.documentData?.template?.hideCommandBar !== true;
    this.displayState.showTemplateName = true;
    this.displayState.showSummaryName = true;
    this.displayState.showPrintButton = this.getFormNavBarField('hidePrintButton') !== true;
    this.displayState.showCameraButton = !this.documentData.isNew && this.getFormNavBarField('hideCameraButton') !== true;
    this.displayState.showCreateButton = canCreate && this.getFormNavBarField('hideCreateButton') !== true;

    this.cdr.detectChanges();
  }

  initializeDisplayState() {
    this.displayState = {
      showCommandBar: false,
      showTemplateName: false,
      showSummaryName: false,
      showCameraButton: false,
      showPrintButton: false,
      showCreateButton: false,
      showDeleteButton: false
    };
  }

  save() {
    this.documentListService.save();
  }

  createNewDocument() {
    this.newDocumentService.createNewDocument();
    this.cdr.detectChanges();
  }

  popupVersionMessage(document) {
    const dateCreated = new Date(document.systemHeader.createdDate).toLocaleDateString('en', {
      year: 'numeric',
      month: 'long',
      day: '2-digit'
    });
    const timeCreated = new Date(document.systemHeader.createdDate).toLocaleTimeString();
    this.notificationService.printMessage('Non-current Version Date: ' + dateCreated + ' ' + timeCreated,
      'info', null, { x: 'right', y: 'top' }, true, false, null, null, true);
  }


}
